import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Typography, Card, CardContent, Grid, Box, Button, Select, MenuItem } from '@mui/material';

const JuzDetails = () => {
  const { juz } = useParams();
  const [juzDetails, setJuzDetails] = useState(null);
  const [translation, setTranslation] = useState(null);
  const [juzList, setJuzList] = useState([]);
  const [selectedJuz, setSelectedJuz] = useState(juz);

  useEffect(() => {
    const fetchJuzDetails = async () => {
      try {
        // Fetch Arabic text
        const arabicResponse = await fetch(`https://api.alquran.cloud/v1/juz/${selectedJuz}/quran-uthmani`);
        const arabicData = await arabicResponse.json();

        // Fetch Translation
        const translationResponse = await fetch(`https://api.alquran.cloud/v1/juz/${selectedJuz}/ur.maududi`);
        const translationData = await translationResponse.json();

        setJuzDetails(arabicData.data);
        setTranslation(translationData.data);
      } catch (error) {
        console.error('Error fetching Juz details:', error);
      }
    };

    fetchJuzDetails();
  }, [selectedJuz]);

  useEffect(() => {
    const fetchJuzList = async () => {
      try {
        const response = await fetch('https://api.alquran.cloud/v1/quran/uthmani');
        const data = await response.json();

        const juzNames = [
          // ... (same juzNames array as in your ByJuz component)
          "Alif-laam-meem (آلم)",
          "Sayaqūlu (سَيَقُولُ)",
          "Tilka ’r-Rusulu (الرُّسُلُ تِلْكَ)",
          "Lan Tana Loo (تَنَالُوا لَنْ)",
          "Wal Mohsanat (وَالْمُحْصَنَاتُ)",
          "Yuhibbullah (اللَّهُ يُحِبُّ لَا)",
          "Wa Iza Samiu (سَمِعُوا وَإِذَا)",
          "Wa Lau Annana (أَنَّنَا وَلَوْ)",
          "Qalal Malao (الْمَلَأُ قَالَ)",
          "Wa A'lamu (وَاعْلَمُوا)",
          "Yatazeroon (يَعْتَذِرُونَ)",
          "Wa Ma Min Da'abat (دَابَّةٍ مِنْ وَمَا)",
          "Wa Ma Ubrioo (بَرِّئُوَمَا)",
          "Rubama (رُبَمَا)",
          "Subhanallazi (الَّذِي سُبْحَانَ)",
          "Qal Alam (أَلَمْ قَالَ)",
          "Aqtarabo (اقْتَرَبَ)",
          "Qadd Aflaha (أَفْلَحَ قَدْ)",
          "Wa Qalallazina (الَّذِينَ وَقَالَ)",
          "A'man Khalaq (خَلَقَ أَمَّنْ)",
          "Utlu Ma Oohi (أُوحِيَ مَا اتْلُ)",
          "Wa Manyaqnut (يَقْنُتْ وَمَنْ)",
          "Wa Mali (لِيَ وَمَا)",
          "Faman Azlam (أَظْلَمُ فَمَنْ)",
          "Elahe Yuruddo (يُرَدُّ إِلَيْهِ)",
          "Ha'a Meem (حم)",
          "Qala Fama Khatbukum (خَطْبُكُمْ فَمَا قَال)",
          "Qadd Sami Allah (اللَّهُ سَمِعَ قَدْ)",
          "Tabarakallazi (الَّذِي تَبَارَكَ)",
          "Amma Yatasa'aloon (يَتَسَاءَلُونَ عَمَّ)"
        ];

        // Assuming data is an array of juz, if not, adjust accordingly
        setJuzList(
          Array.from({ length: 30 }, (_, index) => ({
            number: index + 1,
            name: juzNames[index],
            surahsCount: data.data.surahs.filter(surah => surah.juz === index + 1).length
          }))
        );
      } catch (error) {
        console.error('Error fetching Juz list:', error);
      }
    };

    fetchJuzList();
  }, []);

  const handleNextJuz = () => {
    const nextJuz = parseInt(selectedJuz, 10) + 1;
    setSelectedJuz(nextJuz.toString());
  };

  const handlePrevJuz = () => {
    const prevJuz = parseInt(selectedJuz, 10) - 1;
    setSelectedJuz(prevJuz.toString());
  };

  const handleJuzChange = (event) => {
    setSelectedJuz(event.target.value);
  };

  if (!juzDetails || !translation) {
    // Add loading indicator or return null while data is being fetched
    return null;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '20px' }}>
      <Typography variant="h4" sx={{ color: 'gold', marginBottom: '10px', fontFamily: 'Cinzel Decorative' }}>
        <strong style={{ color: 'darkslategrey' }}> Juz/Parah : </strong> {selectedJuz}
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', width: '100%' }}>
        <Button variant="contained" sx={{ width: '160px', height: '40px', backgroundColor: 'gold', color: 'darkslategrey', marginLeft: '15px' }} onClick={handlePrevJuz}>
          Previous Juz
        </Button>
        <Select value={selectedJuz} onChange={handleJuzChange} sx={{ width: '160px', height: '40px', backgroundColor: 'gold', color: 'darkslategrey' }}>
          {juzList.map((juz) => (
            <MenuItem key={juz.number} value={juz.number}>
              {juz.name}
            </MenuItem>
          ))}
        </Select>
        <Button variant="contained" sx={{ width: '120px', height: '40px', backgroundColor: 'gold', color: 'darkslategrey', marginRight: '15px' }} onClick={handleNextJuz}>
          Next Juz
        </Button>
      </Box>
      <Grid container justifyContent="center" spacing={2}>
        {juzDetails.ayahs.map((ayah, index) => (
          <Grid item xs={12} key={ayah.numberInSurah}>
            <Card style={{ backgroundColor: 'darkslategray', margin: '5px', padding: '10px', textAlign: 'center' }}>
              <CardContent>
                <Typography variant="h3" sx={{ color: 'gold' }}>
                  {ayah.text}
                </Typography>
                <br />
                <Typography variant="h5" sx={{ color: 'whitesmoke', fontWeight: 'lighter', fontStyle: 'oblique' }}>
                  "{translation.ayahs[index].text}"
                </Typography><br />
                <Typography variant="h6" sx={{ fontFamily: 'Cinzel Decorative', color: 'white' }}>
                  <strong style={{ color: 'goldenrod' }}> Verse no. </strong> {ayah.numberInSurah}{' '}
                  <strong style={{ color: 'goldenrod' }}> Surah : </strong> {ayah.surah.englishName}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default JuzDetails;